import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { USER_ROLES } from '@hyperke/shared';
import { message } from 'ant-design-vue';
import { featureFlags } from '@/feature-flags';

/**
 * Route definitions
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false, title: 'Sign In' },
  },
  {
    path: '/client-feedback/appointments/:token',
    name: 'ClientFeedbackAppointments',
    component: () => import('@/views/client-feedback/Appointments.vue'),
    meta: { requiresAuth: false, public: true, title: 'Appointment Feedback' },
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: () => import('@/views/ChangePassword.vue'),
    meta: { requiresAuth: true, title: 'Change Password' },
  },
  {
    path: '/whats-new',
    name: 'WhatsNew',
    component: () => import('@/views/WhatsNew.vue'),
    // No requiresAdmin — the changelog is for every authenticated role
    meta: { requiresAuth: true, title: "What's New" },
  },
  {
    path: '/m',
    name: 'MobileAchievements',
    component: () => import('@/views/mobile/AchievementsPage.vue'),
    // Installable mobile PWA (standalone, no AppLayout shell) — every
    // authenticated role; the API scopes clients to the requester
    meta: { requiresAuth: true, title: 'Attainment' },
  },
  {
    path: '/',
    redirect: '/leads',
  },
  {
    path: '/leads',
    name: 'Leads',
    component: () => import('@/views/Leads.vue'),
    meta: { requiresAuth: true, title: 'Interested Leads' },
  },
  {
    path: '/appointments',
    name: 'Appointments',
    component: () => import('@/views/Appointments.vue'),
    meta: { requiresAuth: true, title: 'Appointment Booked' },
  },
  {
    path: '/outcomes',
    name: 'Outcomes',
    component: () => import('@/views/Outcomes.vue'),
    // PM + ADMIN_ASSISTANT excluded by user decision (2026-10-26): the
    // Handed Over / Samples section is a working-roles surface (IM/SDR/AM
    // seat-scoped) + ADMIN full view. Data-wise these roles still see the
    // same rows in Interested/Appointments — this gates the section only.
    meta: { requiresAuth: true, title: 'Handed Over / Samples', hideForRoles: ['PM', 'ADMIN_ASSISTANT'] },
  },
  {
    path: '/client-feedback-links',
    name: 'ViewClientFeedbackLinks',
    component: () => import('@/views/ViewClientFeedbackLinks.vue'),
    meta: { requiresAuth: true, title: 'Client Feedback Links' },
  },
  {
    path: '/admin/client-leads-tokens',
    name: 'ClientLeadsTokens',
    component: () => import('@/views/admin/ClientLeadsTokens.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Client Lead Tokens' },
  },
  {
    path: '/admin/users',
    name: 'UserManagement',
    component: () => import('@/views/admin/UserManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'User Management' },
  },
  {
    path: '/admin/api-tokens',
    name: 'ApiTokens',
    component: () => import('@/views/admin/ApiTokens.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'API Tokens' },
  },
  {
    path: '/admin/api-audit',
    name: 'ApiAuditLog',
    component: () => import('@/views/admin/ApiAuditLog.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'API Audit Log' },
  },
  {
    path: '/client-leads/:client_code/:token',
    name: 'ClientLeadsPortal',
    component: () => import('@/views/ClientLeadsPortal.vue'),
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/report/:token',
    name: 'ClientReport',
    component: () => import('@/views/public/ClientReport.vue'),
    meta: { requiresAuth: false, public: true, title: 'Appointments Report' },
  },
  {
    path: '/icp-report/:token',
    name: 'IcpReport',
    component: () => import('@/views/public/IcpReport.vue'),
    meta: { requiresAuth: false, public: true, title: 'ICP Appointments Report' },
  },
  // ============================================
  // EMPLOYEE INCENTIVES ROUTES
  // ============================================
  {
    path: '/incentives/my-performance',
    name: 'MyIncentives',
    component: () => import('@/views/incentives/MyPerformance.vue'),
    meta: { requiresAuth: true, title: 'My Incentives' },
  },
  {
    path: '/my-pay',
    name: 'my-pay',
    component: () => import('@/views/my-pay/MyPayPage.vue'),
    meta: { requiresAuth: true, title: 'My Pay' },
    // Feature-flagged page: unreachable (redirects to My Incentives) while
    // featureFlags.myPay.enabled is false. The nav entry is hidden too.
    beforeEnter: (to, from, next) => {
      if (!featureFlags.myPay.enabled) {
        next({ name: 'MyIncentives', replace: true });
        return;
      }
      next();
    },
  },
  {
    path: '/im-analytics',
    name: 'MyPerformance',
    component: () => import('@/views/MyPerformance.vue'),
    meta: { requiresAuth: true, title: 'My Performance' },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;

      // Admins should access the admin analytics view instead
      if (user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.ADMIN_ASSISTANT) {
        message.info('Redirecting to full admin analytics...');
        next({ name: 'IMAnalytics', replace: true });
      } else {
        next();
      }
    },
  },
  {
    path: '/admin/audit-logs',
    name: 'AuditLogs',
    component: () => import('@/views/admin/AuditLogs.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Audit Logs' },
  },
  {
    path: '/admin/client-feedback-links',
    name: 'ClientFeedbackLinks',
    component: () => import('@/views/admin/ClientFeedbackLinks.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Client Management' },
  },
  {
    path: '/admin/report-links',
    name: 'ReportLinks',
    component: () => import('@/views/admin/ReportLinks.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Public Report Links' },
  },
  {
    path: '/admin/client-assignment-overrides',
    name: 'ClientAssignmentOverrides',
    component: () => import('@/views/admin/ClientAssignmentOverrides.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Client Co-assignments' },
  },
  {
    path: '/admin/icp',
    name: 'IcpClassifier',
    component: () => import('@/views/admin/IcpClassifier.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'ICP Classifier (pilot)' },
  },
  {
    path: '/admin/webhook-integrations',
    name: 'WebhookIntegrations',
    component: () => import('@/views/admin/WebhookIntegrations.vue'),
    meta: { requiresAuth: true, title: 'Webhook Integrations' },
    // ADMIN + PM get full access; ADMIN_ASSISTANT is read-only (the API
    // enforces the real gate — mutating endpoints 403 for assistants).
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;

      if (
        user?.role === USER_ROLES.ADMIN ||
        user?.role === USER_ROLES.PM ||
        user?.role === USER_ROLES.ADMIN_ASSISTANT
      ) {
        next();
      } else {
        message.info('Access restricted to admins and product managers');
        next({ name: 'Leads', replace: true });
      }
    },
  },
  {
    path: '/admin/forwarding',
    name: 'AdminForwarding',
    component: () => import('@/views/admin/Forwarding.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Forwarding' },
  },
  {
    path: '/admin/ai-insights',
    name: 'AIInsights',
    component: () => import('@/views/admin/AIInsights.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'AI Insights' },
  },
  {
    path: '/admin/deleted-leads',
    name: 'DeletedLeads',
    component: () => import('@/views/admin/DeletedLeads.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Deleted Leads' },
  },
  {
    path: '/admin/calendly-review',
    name: 'CalendlyReview',
    component: () => import('@/views/admin/CalendlyReview.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Calendly Review' },
  },
  {
    path: '/admin/dtc-dashboard',
    name: 'DtcDashboard',
    component: () => import('@/views/admin/DtcDashboard.vue'),
    meta: { requiresAuth: true, dtcAccess: true, title: 'DTC Dashboard' },
  },
  {
    path: '/admin/im-analytics',
    name: 'IMAnalytics',
    component: () => import('@/views/admin/IMAnalytics.vue'),
    meta: { requiresAuth: true, title: 'IM Performance' },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;

      // IMs should access their personal analytics view instead
      if (user?.role === USER_ROLES.IM) {
        message.info('Redirecting to your personal analytics...');
        next({ name: 'MyPerformance', replace: true });
      } else {
        next();
      }
    },
  },
  {
    path: '/admin/sdr-performance',
    name: 'SDRAnalytics',
    component: () => import('@/views/admin/SDRAnalytics.vue'),
    // No requiresAdmin: AMs get the lead-level view too — the API scopes their
    // data to their assigned accounts (services/analytics/am-scope.ts)
    meta: { requiresAuth: true, title: 'SDR Performance' },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;
      if (user?.role === USER_ROLES.SDR) {
        message.info('Redirecting to your SDR performance...');
        next({ name: 'MySDRPerformance', replace: true });
      } else if (
        user?.role === USER_ROLES.ADMIN ||
        user?.role === USER_ROLES.ADMIN_ASSISTANT ||
        user?.role === USER_ROLES.PM ||
        user?.role === USER_ROLES.AM
      ) {
        next();
      } else {
        // IM has no scoping path here (previously blocked by requiresAdmin)
        message.info('Access restricted to admins and account managers');
        next({ name: 'Leads', replace: true });
      }
    },
  },
  {
    path: '/my-sdr-performance',
    name: 'MySDRPerformance',
    component: () => import('@/views/MySDRPerformance.vue'),
    meta: { requiresAuth: true, title: 'My SDR Performance' },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;
      if (user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.ADMIN_ASSISTANT) {
        next({ name: 'SDRAnalytics', replace: true });
      } else {
        next();
      }
    },
  },
  {
    path: '/admin/im-work-schedules',
    name: 'IMWorkSchedules',
    component: () => import('@/views/admin/IMWorkSchedules.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Work Schedules' },
  },
  {
    path: '/admin/active-impersonations',
    name: 'ActiveImpersonations',
    component: () => import('@/views/admin/ActiveImpersonations.vue'),
    // adminOnly: the impersonation APIs are ADMIN-only — assistants would
    // only land on a permanent error state here.
    meta: { requiresAuth: true, requiresAdmin: true, adminOnly: true, title: 'Active Impersonations' },
  },
  // ============================================
  // CALENDLY IMPORT ROUTE
  // ============================================
  {
    path: '/admin/calendly-import',
    name: 'CalendlyImport',
    component: () => import('@/views/admin/CalendlyImport.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Calendly Import' },
  },
  // ============================================
  // INCENTIVES ADMIN ROUTES
  // ============================================
  {
    path: '/admin/incentives',
    name: 'IncentivesAdmin',
    redirect: '/admin/incentives/periods',
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Incentives' },
  },
  {
    path: '/admin/incentives/periods',
    name: 'IncentivesPeriods',
    component: () => import('@/views/admin/incentives/PeriodDashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Incentive Periods' },
  },
  {
    path: '/admin/incentives/configs',
    name: 'IncentivesConfigs',
    component: () => import('@/views/admin/incentives/ClientConfigManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Incentive Configs' },
  },
  {
    path: '/admin/incentives/payouts',
    name: 'IncentivesPayouts',
    component: () => import('@/views/admin/incentives/PayoutManagementPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Incentive Payouts' },
  },
  {
    path: '/admin/incentives/comp-preview',
    name: 'admin-comp-preview',
    component: () => import('@/views/admin/incentives/CompPreviewPage.vue'),
    // Same gate as the neighboring admin incentive pages (ADMIN +
    // ADMIN_ASSISTANT via requiresAdmin).
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Compensation Preview' },
    // Feature-flagged Gate B page: unreachable (redirects to the admin
    // incentives landing) while featureFlags.myPay.enabled is false. The
    // Payout Management header entry link is hidden too.
    beforeEnter: (to, from, next) => {
      if (!featureFlags.myPay.enabled) {
        next({ path: '/admin/incentives', replace: true });
        return;
      }
      next();
    },
  },
  {
    path: '/admin/incentives/audit-log',
    name: 'IncentivesAuditLog',
    component: () => import('@/views/admin/incentives/IncentivesAuditLog.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Incentive Audit Log' },
  },
  // ============================================
  // PM CLIENT ACHIEVEMENTS ROUTE
  // ============================================
  {
    path: '/pm/client-achievements',
    name: 'PMClientAchievements',
    component: () => import('@/views/pm/ClientAchievementsPage.vue'),
    meta: { requiresAuth: true, title: 'Client Achievements' },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;

      // Only allow PM role
      if (user?.role === USER_ROLES.PM) {
        next();
      } else {
        // Redirect non-PM users to leads page
        message.info('Access restricted to Product Managers');
        next({ name: 'Leads', replace: true });
      }
    },
  },
  // ============================================
  // SHOW-RATE REPORT ROUTE
  // ============================================
  {
    path: '/reports/show-rate',
    name: 'ShowRate',
    component: () => import('@/views/reports/ShowRate.vue'),
    meta: { requiresAuth: true, title: 'Show Rate' },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;

      // Every role may view; IM/SDR/AM are scoped to assigned clients by the API.
      if (user && ['ADMIN', 'ADMIN_ASSISTANT', 'PM', 'AM', 'IM', 'SDR'].includes(user.role)) {
        next();
      } else {
        message.info('Access restricted');
        next({ name: 'Leads', replace: true });
      }
    },
  },
  // ============================================
  // AM TEAM PERFORMANCE ROUTE
  // ============================================
  {
    path: '/am/team-performance',
    name: 'AMTeamPerformance',
    component: () => import('@/views/am/TeamPerformance.vue'),
    meta: { requiresAuth: true, title: 'Team Performance' },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;

      // AM: scoped to assigned accounts; PM: all accounts (admin-family).
      // Admins can QA via impersonation; the API enforces the real gate.
      if (user?.role === USER_ROLES.AM || user?.role === USER_ROLES.PM) {
        next();
      } else {
        message.info('Access restricted to Account Managers');
        next({ name: 'Leads', replace: true });
      }
    },
  },
];

/**
 * Vue Router instance with auth guard
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Auth guard
// P0.2 Fix: Enhanced route guard to prevent access to protected pages after logout
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();

  // Check authentication from multiple sources for reliability
  const storeToken = authStore.token;
  const storeUser = authStore.user;
  const localStorageToken = localStorage.getItem('token');
  const localStorageUser = localStorage.getItem('user');

  // DIAGNOSTIC: Log detailed state before any checks
  console.log('[Router Guard] AUTH STATE CHECK', {
    route: { to: to.name, from: from.name },
    store: {
      hasToken: !!storeToken,
      tokenPrefix: storeToken ? storeToken.substring(0, 20) + '...' : 'none',
      hasUser: !!storeUser,
      userRole: storeUser?.role,
      userEmail: storeUser?.email,
    },
    localStorage: {
      hasToken: !!localStorageToken,
      tokenPrefix: localStorageToken ? localStorageToken.substring(0, 20) + '...' : 'none',
      hasUser: !!localStorageUser,
    },
    comparison: {
      tokensMatch: storeToken === localStorageToken,
      usersMatch: JSON.stringify(storeUser) === localStorageUser,
      tokenMismatch: (storeToken && !localStorageToken) || (!storeToken && localStorageToken),
    }
  });

  // User is authenticated only if BOTH store and localStorage have valid token/user
  const isAuthenticated = !!(storeToken && storeUser && localStorageToken && localStorageUser);

  // If store and localStorage are out of sync, clear both (security measure)
  if ((storeToken && !localStorageToken) || (!storeToken && localStorageToken)) {
    const debugInfo = {
      timestamp: new Date().toISOString(),
      type: 'router_guard_token_mismatch',
      route: { to: to.name, from: from.name, fullPath: to.fullPath },
      storeState: {
        hasToken: !!storeToken,
        tokenPrefix: storeToken ? storeToken.substring(0, 20) + '...' : 'none',
        hasUser: !!storeUser,
        userRole: storeUser?.role,
        userEmail: storeUser?.email,
      },
      localStorageState: {
        hasToken: !!localStorageToken,
        tokenPrefix: localStorageToken ? localStorageToken.substring(0, 20) + '...' : 'none',
        hasUser: !!localStorageUser,
      },
      mismatchType: storeToken && !localStorageToken ? 'store_exists_localstorage_missing' : 'localstorage_exists_store_missing',
    };

    // Write to sessionStorage for persistent debugging
    sessionStorage.setItem('__hyperke_router_guard_debug__', JSON.stringify(debugInfo));

    console.warn('[Router Guard] Token mismatch - persistent log written to __hyperke_router_guard_debug__', {
      storeToken: !!storeToken,
      localStorageToken: !!localStorageToken,
      mismatchType: storeToken && !localStorageToken ? 'store exists, localStorage missing' : 'localStorage exists, store missing',
      route: to.name,
    });

    authStore.logout();
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }
  
  // Check must_change_password from both store and localStorage (for reliability)
  const storeMustChange = authStore.user?.must_change_password === true;
  let localStorageMustChange = false;
  try {
    if (localStorageUser) {
      const parsedUser = JSON.parse(localStorageUser);
      localStorageMustChange = parsedUser?.must_change_password === true;
    }
  } catch (e) {
    // Ignore parse errors
  }
  
  const mustChangePassword = storeMustChange || localStorageMustChange;

  console.log('[Router Guard]', {
    to: to.name,
    from: from.name,
    isAuthenticated,
    hasStoreToken: !!storeToken,
    hasLocalStorageToken: !!localStorageToken,
    storeMustChange,
    localStorageMustChange,
    mustChangePassword,
  });

  // P0.2 Fix: If route requires auth but user is not authenticated, redirect to login
  // Exception: Public routes (like client feedback) don't require auth
  if (to.meta.requiresAuth && !isAuthenticated && !to.meta.public) {
    console.log('[Router Guard] Protected route requires auth, redirecting to login');
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }

  // Check if route requires admin role (ADMIN or ADMIN_ASSISTANT).
  // meta.adminOnly narrows the gate to ADMIN only — those routes back onto
  // ADMIN-only APIs where assistants would just see errors.
  if (to.meta.requiresAdmin && isAuthenticated) {
    const userRole = authStore.user?.role;
    if (userRole !== 'ADMIN' && (to.meta.adminOnly || userRole !== 'ADMIN_ASSISTANT')) {
      console.log('[Router Guard] Admin route accessed by non-admin user');
      next({ name: 'Leads' }); // Redirect to leads if not admin
      return;
    }
  }

  // DTC dashboard: admin/assistant OR accounts granted via the allowlist
  // (mirrors the backend canAccessDtcDashboard check; the API enforces the real gate).
  if (to.meta.dtcAccess && isAuthenticated) {
    const u = authStore.user;
    const ok =
      u?.role === 'ADMIN' ||
      u?.role === 'ADMIN_ASSISTANT' ||
      u?.dtc_dashboard_access === true;
    if (!ok) {
      console.log('[Router Guard] DTC dashboard access denied');
      next({ name: 'Leads' });
      return;
    }
  }

  // Role-excluded routes (meta.hideForRoles): redirect to Leads instead of
  // rendering a section the role shouldn't see (nav item is hidden too).
  if (Array.isArray(to.meta.hideForRoles) && isAuthenticated) {
    const userRole = authStore.user?.role;
    if (userRole && (to.meta.hideForRoles as string[]).includes(userRole)) {
      console.log('[Router Guard] Route hidden for role:', { route: to.name, userRole });
      next({ name: 'Leads' });
      return;
    }
  }
  
  // If user is already logged in and tries to access login page, redirect to leads
  if (to.name === 'Login' && isAuthenticated) {
    // If user must change password, redirect to change password page
    if (mustChangePassword) {
      console.log('[Router Guard] Redirecting to ChangePassword from Login');
      next({ name: 'ChangePassword', replace: true });
    } else {
      next({ name: 'Leads', replace: true });
    }
    return;
  }
  
  // Force redirect to change password if user must change password
  if (to.name !== 'ChangePassword' && isAuthenticated && mustChangePassword) {
    console.log('[Router Guard] Force redirecting to ChangePassword');
    next({ name: 'ChangePassword', replace: true });
    return;
  }
  
  next();
});

// Per-page browser tab <title>.
// Routes with meta.title show "Page · Hyperke"; others fall back to the app
// default (so Interested Leads / Appointment Booked stay unchanged).
router.afterEach((to) => {
  const title = to.meta?.title as string | undefined;
  document.title = title ? `${title} · Hyperke` : 'Hyperke Smartlead Dashboard';
});

export default router;